import { useCallback, useState } from 'react'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Loading from '@/elements/Loading'
import Table from '@/components/Table'
import Pagination from '@/components/Pagination'
import Modal from '@/components/Modal'
import usePagination from '@/hooks/usePagination'
import useFiltering from '@/hooks/useFiltering'
import useOrganizationIndividualForm from '@/hooks/forms/useOrganizationIndividualForm'
import useBreakpoints from '@/hooks/useBreakpoints'
import {
  useOrganizationIndividualsTable,
  type OrganizationIndividualTableType
} from '@/hooks/tables/organizationIndividualsTable'
import useDeactivateUser from '@/hooks/useDeactivateUser'
import { createOrganizationIndividual, useGetOrganizationIndividuals } from '@/api/organizationIndividual'
import toast from '@/utils/toast'

import OrganizationIndividualsFilterBar from './OrganizationIndividualsFilterBar'
import OrganizationIndividualForm from './OrganizationIndividualForm'
import { getOrgUserInfo } from '@/api/user'

function OrganizationIndividuals() {
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [individualToDeleteId, setIndividualToDeleteId] = useState<string | null>(null)
  const { isTablet, isMobile } = useBreakpoints()
  const { page, limit, setPage } = usePagination(10)
  const { updateFilterBy, allFilters } = useFiltering<['name']>()
  const { data: organizationIndividuals, refetch, isLoading } = useGetOrganizationIndividuals({
    page,
    limit,
    filterBy: allFilters.name
  })
  const { form, resetForm } = useOrganizationIndividualForm(null)
  const {
    setShowDeactivateModal,
    DeactivateUserModal
  } = useDeactivateUser({
    userType: 'individual',
    userId: individualToDeleteId || '',
    refetch
  })
  const tableData = useOrganizationIndividualsTable(
    organizationIndividuals?.data?.items || [],
    async (id: string) => {
      try {
        const data = await getOrgUserInfo({ subjectId: id })
        const organizationIndividualId = data.data?.organizationIndividual?.id
        if (!organizationIndividualId) return
        setIndividualToDeleteId(organizationIndividualId)
        setShowDeactivateModal(true)
      } catch {
        toast.error('Failed to fetch individual information. Please try again.')
      }
    }
  )

  const submitNewIndividual = useCallback(async () => {
    const isValid = await form.trigger()
    const dirtyFields = form.formState.dirtyFields
    if (isValid) {
      const values = form.getValues()
      const formData = new FormData()
      Object.entries({
        ...(dirtyFields.avatar && { avatar: values.avatar || '' }),
        firstName: values.firstName,
        lastName: values.lastName || '',
        email: values.email,
        city: values.city || '',
        state: values.state || '',
        groupName: values.groupName || '',
        specialty: values.specialty || ''
      }).forEach(([key, value]) => formData.append(key, value))

      try {
        await createOrganizationIndividual(formData)
        setShowAddUserModal(false)
        resetForm(null)
        toast.success('Individual added successfully!')
        refetch()
      } catch {
        toast.error('You are attempting to add a user that has already been set-up in the system. If you require further assistance please reach out to your Fuzzy Watchdog Support Team.')
      }
    }
  }, [form, resetForm, refetch])

  return (
    <Flex
      className='card'
      wfull
      gap={7}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      <Flex gap={2}>
        <Text variant='h3'>Individuals</Text>
        <Text color='black-light-40'>
          Manage individuals of your organization and their assigned delegates.
        </Text>
      </Flex>
      <Flex wfull gap={4}>
        <OrganizationIndividualsFilterBar
          filterByName={allFilters.name || ''}
          setFilterByName={q => updateFilterBy('name', q)}
          refetchIndividuals={refetch}
          handleOpenAddUserModal={() => setShowAddUserModal(true)}
        />
        <Modal
          title='Add Individual'
          isOpen={showAddUserModal}
          onCancel={() => {
            setShowAddUserModal(false)
            form.reset()
          }}
          onSave={submitNewIndividual}
          saveLabel='Send Invitation'
          disabled={!form.formState.isDirty}
          style={{ width: 538 }}
          preventClickOutsideClose={form.formState.isDirty}
        >
          <OrganizationIndividualForm form={form} mode='add' onSubmit={submitNewIndividual} />
        </Modal>
        {isLoading ? (
          <Flex wfull align='center' justify='center' py={8}>
            <Loading />
          </Flex>
        ) : (
          <>
            <Table<OrganizationIndividualTableType> {...tableData} />
            <DeactivateUserModal />
          </>
        )}
        <Flex wfull align='center' pt={4} mt='auto'>
          <Pagination
            variant={isMobile ? 'compact' : 'default'}
            onPageChange={setPage}
            {...organizationIndividuals?.data?.pagination}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
export default OrganizationIndividuals
